// Copyright (c) StrangeBee 2024

import { ProcedureController, ProcedureInput, ProcedureOutput, ProcedureMultiInput } from 'thehive-sdk';

export interface TTPEventList {
    onCreate: (procedure: ProcedureMultiInput, caseId: string) => Promise<ProcedureOutput | ProcedureOutput[] | void>;
    onCreateForAlert: (
        procedure: ProcedureMultiInput,
        alertId: string,
    ) => Promise<ProcedureOutput | ProcedureOutput[] | void>;
    onEdit: (procedure: Partial<ProcedureInput>, procedureId: string) => Promise<void>;
    onDelete: (procedureId: string) => Promise<void>;
    onDeleteOnAlert: (procedureId: string, alertId: string) => Promise<void>;
    onBulkDelete: (procedureListIds: string[]) => Promise<void>;
}

interface TTPEventListParams {
    locale: (label: string) => string;
    controller: ProcedureController;
    onSuccess: (count?: boolean, message?: string) => void;
    onError: (label?: string) => void;
}

export const ttpEventList = ({ controller, locale, onError, onSuccess }: TTPEventListParams): TTPEventList => ({
    onCreate: ({ patternList, ...rest }, caseId) => {
        const procedures = patternList.map(({ patternId, tactic }) => ({ ...rest, patternId, tactic }));

        return controller
            .bulkCreate({ procedures }, caseId)
            .then(() => onSuccess(true, locale('notification.ttp.create')))
            .catch(onError);
    },
    onCreateForAlert: ({ patternList, ...rest }, alertId) => {
        const procedures = patternList.map(({ patternId, tactic }) => ({ ...rest, patternId, tactic }));

        return controller
            .bulkCreateOnAlert({ procedures }, alertId)
            .then(() => onSuccess(true, locale('notification.ttp.create')))
            .catch(onError);
    },
    onEdit: (procedure, procedureId): Promise<void> => {
        return controller
            .edit<Partial<ProcedureInput>, void>(procedure, procedureId)
            .then(() => onSuccess(false, locale('notification.ttp.edit')))
            .catch(onError);
    },
    onDelete: (procedureId): Promise<void> => {
        return controller
            .delete(procedureId)
            .then(() => onSuccess(true, locale('notification.ttp.delete.single')))
            .catch(onError);
    },
    onDeleteOnAlert: (procedureId, alertId): Promise<void> => {
        return controller
            .deleteOnAlert(procedureId, alertId)
            .then(() => onSuccess(true, locale('notification.ttp.delete.single')))
            .catch(onError);
    },
    onBulkDelete: (procedureListIds): Promise<void> => {
        const isMultiple = procedureListIds.length > 1;

        return controller
            .bulkDelete(procedureListIds)
            .then(() =>
                onSuccess(
                    true,
                    locale(isMultiple ? 'notification.ttp.delete.multiple' : 'notification.ttp.delete.single'),
                ),
            )
            .catch(onError);
    },
});
